<template>
  <div>
    <v-dialog v-if="show && !minimized" :value="show && !minimizing" @input="v => { if(!v) minimize() }" max-width="920" persistent>
      <v-card class="admin-onboarding-card" :class="{ 'admin-onboarding-card--minimizing': minimizing }" dark>
        <template v-if="get_done">
          <div class="admin-onboarding-completed">
            <v-avatar color="#25D366" size="72">
              <v-icon dark x-large>mdi-check-circle</v-icon>
            </v-avatar>
            <div class="admin-onboarding-completed-text">
              <h2>Tudo pronto!</h2>
              <p>Você concluiu todas as etapas essenciais.</p>
              <p class="admin-onboarding-completed-hint">Seu sistema já está configurado para vender, emitir documentos e gerenciar sua operação.</p>
            </div>
            <v-btn color="#25D366" large dark depressed @click="finish">
              <v-icon left>mdi-rocket-launch</v-icon>
              Finalizar onboarding
            </v-btn>
          </div>
        </template>
        <template v-else>
          <div class="admin-onboarding-hero">
            <v-avatar color="#25D366" size="54"><v-icon dark large>mdi-rocket-launch-outline</v-icon></v-avatar>
            <div>
              <div class="overline green--text text--accent-3">Primeiro acesso do admin</div>
              <h2>Vamos deixar sua operação pronta?</h2>
              <p>Complete os passos essenciais para vender, imprimir e acompanhar tudo em tempo real.</p>
            </div>
          </div>
          <div class="admin-onboarding-layout">
            <div class="admin-onboarding-steps">
              <button
                v-for="step in get_steps"
                :key="step.key"
                class="admin-onboarding-step"
                :class="{ active: stepKey === step.key, done: completed[step.key] }"
                @click="open_step({ step, router: $router })"
              >
                <v-icon :color="completed[step.key] ? '#25D366' : '#9be7bd'">
                  {{ completed[step.key] ? "mdi-check-circle" : step.icon }}
                </v-icon>
                <span>
                  <strong>{{ step.title }}</strong>
                  <small>{{ step.description }}</small>
                </span>
                <v-icon color="#9be7bd">mdi-chevron-right</v-icon>
              </button>
            </div>
            <aside class="admin-onboarding-guide" v-if="get_active_step">
              <div class="admin-onboarding-guide-icon">
                <v-icon color="#25D366" size="30">{{ get_active_step.icon }}</v-icon>
              </div>
              <div class="overline green--text text--accent-3">Etapa guiada</div>
              <h3>{{ get_active_step.title }}</h3>
              <p>{{ get_active_step.guide }}</p>
              <div class="admin-onboarding-tips">
                <span v-for="tip in get_active_step.tips" :key="tip">
                  <v-icon size="15" color="#9be7bd">mdi-check</v-icon>{{ tip }}
                </span>
              </div>
              <div class="admin-onboarding-actions">
                <v-btn color="#25D366" dark depressed @click="open_step({ step: get_active_step, router: $router })">
                  <v-icon left small>mdi-open-in-new</v-icon>Abrir etapa
                </v-btn>
                <v-btn outlined color="#9be7bd" @click="complete_step(get_active_step)">
                  Marcar concluida
                </v-btn>
              </div>
            </aside>
          </div>
          <v-card-actions>
            <span class="admin-onboarding-progress">{{ get_progress_text }}</span>
            <v-spacer />
            <v-btn text color="#9be7bd" @click="finish">Fazer depois</v-btn>
            <v-btn color="#25D366" :disabled="!get_done" @click="finish">Concluir onboarding</v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>

    <transition name="badge">
      <div v-if="show && minimized" class="admin-onboarding-floating">
        <div class="admin-onboarding-badge" @click="expand">
          <v-avatar color="#25D366" size="36">
            <v-icon dark small>mdi-rocket-launch-outline</v-icon>
          </v-avatar>
          <div class="admin-onboarding-badge-text">
            <span class="admin-onboarding-badge-label">Onboarding</span>
            <span class="admin-onboarding-badge-progress">{{ get_progress_text }}</span>
          </div>
          <v-icon small color="#9be7bd" class="admin-onboarding-badge-chevron">mdi-chevron-down</v-icon>
        </div>
        <div class="admin-onboarding-guidance" @click="expand">
          <v-icon small color="#9be7bd">mdi-check-circle-outline</v-icon>
          <span>Ao concluir este passo, clique aqui para voltar ao onboarding</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";

export default {
  computed: {
    ...mapState("AdminOnboarding", ["stepKey", "completed", "minimized", "minimizing"]),
    show: {
      get() { return this.$store.state.AdminOnboarding.show; },
      set(v) { this.$store.commit("AdminOnboarding/SET_SHOW", v); }
    },
    ...mapGetters("AdminOnboarding", [
      "get_steps", "get_active_step", "get_done", "get_progress_text"
    ])
  },
  methods: {
    ...mapActions("AdminOnboarding", [
      "complete_step", "finish", "open_step", "minimize", "expand"
    ])
  }
};
</script>

<style scoped>
.admin-onboarding-card { border-radius: 24px !important; overflow: hidden; background: linear-gradient(135deg, #182229, #101714) !important; border: 1px solid rgba(37, 211, 102, .28); box-shadow: 0 24px 70px rgba(0,0,0,.45) !important; transition: transform .35s ease, opacity .35s ease, filter .35s ease; }
.admin-onboarding-card--minimizing { transform: scale(0.2) translateY(-160px); opacity: 0; filter: blur(12px); }
.admin-onboarding-hero { display: flex; gap: 16px; padding: 24px 24px 12px; }
.admin-onboarding-hero h2 { margin: 0 0 6px; color: #eef4f1; }
.admin-onboarding-hero p { margin: 0; color: #b7d4c7; }
.admin-onboarding-layout { display: grid; grid-template-columns: minmax(0, 1fr) 320px; gap: 14px; padding: 16px 24px 8px; }
.admin-onboarding-steps { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.admin-onboarding-step { display: grid; grid-template-columns: 32px minmax(0, 1fr) 24px; align-items: center; gap: 10px; padding: 14px; border: 1px solid rgba(155,231,189,.16); border-radius: 18px; background: rgba(255,255,255,.055); color: #eef4f1; text-align: left; transition: .18s ease; cursor: pointer; }
.admin-onboarding-step:hover { transform: translateY(-2px); border-color: rgba(37,211,102,.48); background: rgba(37,211,102,.10); }
.admin-onboarding-step.active { border-color: rgba(37,211,102,.62); background: rgba(37,211,102,.14); box-shadow: inset 0 0 0 1px rgba(37,211,102,.12); }
.admin-onboarding-step.done { background: rgba(37,211,102,.08); }
.admin-onboarding-step strong, .admin-onboarding-step small { display: block; }
.admin-onboarding-step small { color: #b7d4c7; }
.admin-onboarding-guide { background: rgba(11,20,24,.72); border: 1px solid rgba(155,231,189,.16); border-radius: 22px; padding: 16px; }
.admin-onboarding-guide-icon { align-items: center; background: rgba(37,211,102,.10); border-radius: 16px; display: flex; height: 48px; justify-content: center; margin-bottom: 10px; width: 48px; }
.admin-onboarding-guide h3 { color: #eef4f1; margin: 0 0 8px; }
.admin-onboarding-guide p { color: #b7d4c7; line-height: 1.45; margin: 0 0 12px; }
.admin-onboarding-tips { display: grid; gap: 7px; margin-bottom: 14px; }
.admin-onboarding-tips span { align-items: center; color: #d9eee4; display: flex; font-size: 12px; gap: 7px; }
.admin-onboarding-actions { display: grid; gap: 8px; }
.admin-onboarding-progress { color: #b7d4c7; font-size: 12px; padding-left: 12px; }
@media(max-width: 860px) { .admin-onboarding-layout { grid-template-columns: 1fr; } .admin-onboarding-steps { grid-template-columns: 1fr; } .admin-onboarding-hero { flex-direction: column; } }

.admin-onboarding-floating {
  position: fixed; top: 20px; left: 50%; z-index: 9999;
  display: flex; flex-direction: column; align-items: center; gap: 8px;
}
.admin-onboarding-badge {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 18px 8px 8px;
  background: linear-gradient(135deg, #182229, #101714);
  border: 1px solid rgba(37, 211, 102, .28);
  border-radius: 40px; box-shadow: 0 8px 30px rgba(0,0,0,.4);
  cursor: pointer; transition: .2s ease;
  position: relative; left: -50%;
}
.admin-onboarding-badge:hover { transform: translateY(-2px); border-color: rgba(37,211,102,.48); }
.admin-onboarding-badge-chevron { transition: transform .2s ease; }
.admin-onboarding-badge:hover .admin-onboarding-badge-chevron { transform: translateY(2px); }
.admin-onboarding-badge-text { display: flex; flex-direction: column; line-height: 1.3; }
.admin-onboarding-badge-label { color: #eef4f1; font-size: 13px; font-weight: 600; }
.admin-onboarding-badge-progress { color: #b7d4c7; font-size: 11px; }

.admin-onboarding-guidance {
  display: flex; align-items: center; gap: 6px;
  padding: 6px 14px;
  background: rgba(16,23,20,.88);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(155,231,189,.20);
  border-radius: 20px;
  color: #cbe3d7; font-size: 12px;
  cursor: pointer; transition: .2s ease;
  white-space: nowrap;
  position: relative; left: -50%;
}
.admin-onboarding-guidance:hover { border-color: rgba(37,211,102,.40); background: rgba(37,211,102,.10); }

.admin-onboarding-completed {
  display: flex; flex-direction: column; align-items: center; gap: 20px;
  padding: 48px 32px 40px; text-align: center;
}
.admin-onboarding-completed-text h2 {
  color: #eef4f1; margin: 0 0 8px; font-size: 26px;
}
.admin-onboarding-completed-text p {
  color: #b7d4c7; margin: 0; font-size: 15px; max-width: 400px;
}
.admin-onboarding-completed-hint {
  margin-top: 6px !important; font-size: 13px !important; color: #8aab9c !important;
}

.badge-enter-active { animation: badge-in .45s ease-out .08s both; }
.badge-leave-active { animation: badge-in .2s ease-in reverse; }
@keyframes badge-in {
  0% { opacity: 0; transform: translateY(-28px) scale(.85); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}
</style>
